import { useEffect, useState } from 'react'
import { RouterProvider } from 'react-router-dom'
import { Provider, ErrorBoundary } from '@rollbar/react'
import ChakraBaseProvider from './components/ui/provider'
import useStore from './store'
import requestInterceptor from './utilities/requestInterceptor'
import rollbarConfig from './configs/rollbarConfig'
import router from './router'
import { MsalProvider } from '@azure/msal-react'
import getPushNotificationToken from './utilities/pushNotificationToken'
import { pushNotificationsEnabled } from './utilities/featureFlags'
import { initializeForegroundNotifications } from './notifications/foregroundNotifications'
import HubspotChat from './components/common/HubspotChat'
import { hubspotLiveChatEnabled } from './utilities/featureFlags'
import ReusableModal from './components/common/ReusableModal'
import useDisclosure from './hooks/useDisclosure'
import TosModal from './components/common/TosModal'

export default function App({ msalInstance }) {
  const user = useStore((state) => state.user)
  const setIsOnline = useStore((state) => state.setIsOnline)
  const createActionCableConsumer = useStore((state) => state.createActionCableConsumer)
  const updateConsumer = useStore((state) => state.updateConsumer)
  const subscribeToNotifications = useStore((state) => state.subscribeToNotifications)
  const [rollbarConfiguration, setRollbarConfiguration] = useState(rollbarConfig(user))
  const createPushNotificationDevice = useStore((state) => state.createPushNotificationDevice)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const acceptTermsOfService = useStore((state) => state.acceptTermsOfService)
  const dealer = useStore((state) => state.dealer)

  // Creates an action cable connection
  useEffect(() => {
    if (user && user.type === 'DealerUser') {
      const consumer = createActionCableConsumer()
      updateConsumer(consumer)
      subscribeToNotifications()
    }
  }, [user])

  // Resets rollbar configuration after user login to include user data
  useEffect(() => {
    setRollbarConfiguration(rollbarConfig(user))
  }, [user])

  useEffect(() => {
    if(user && user.type === 'DealerUser') {
      if (pushNotificationsEnabled && (user.branchId === 3 || user.branchId === 17)) {
        getPushNotificationToken(user, createPushNotificationDevice)
        initializeForegroundNotifications(user)
      }
    }
  }, [user, pushNotificationsEnabled])

  requestInterceptor()

  useEffect(()=> {
    setIsOnline(navigator.onLine)

    window.addEventListener('online', function(e) { setIsOnline(true) })
    window.addEventListener('offline', function(e) { setIsOnline(false) })

    return () => {
      window.removeEventListener("online", function(e) { setIsOnline(true) })
      window.removeEventListener("offline", function(e) { setIsOnline(false) })
  }
  },[])

  const displayLiveChat = () => {
    if (hubspotLiveChatEnabled(user) && user.type === 'DealerUser') {
      return <HubspotChat hubId={20173262} user={user} />
    }
    return null
  }

  const handleAgreementAccept = async () => {
    await acceptTermsOfService(user?.dealerUserId, dealer?.id)
  }

  return (
    <Provider config={rollbarConfiguration}>
      <ErrorBoundary>
        <ChakraBaseProvider>
          <MsalProvider instance={msalInstance}>
            {displayLiveChat()}
            {user?.type === 'DealerUser' && !user.accepted_new_terms_at &&
              <TosModal handleAccept={handleAgreementAccept} isOpen={isOpen} onOpen={onOpen} onClose={onClose}/>
            }
            <RouterProvider router={router} />
          </MsalProvider>
        </ChakraBaseProvider>
      </ErrorBoundary>
    </Provider>
  )
}